import { Button, message } from 'antd';
import { RcFile } from 'antd/es/upload';
import axios, { AxiosProgressEvent } from 'axios';
import React, { useRef } from 'react';
import { BlobIndexTag, BlobMetadataField } from '../../../generated';
import { AuthResult, AzureBlobResponse } from '../file-upload/azure-upload';
import { FileValidator } from '../file-upload/file-validator';

interface GeneratePdfFormButtonProps {
  authorizeRequest: (file: File) => Promise<AuthResult>;
  onSuccess: (response: AzureBlobResponse) => Promise<void>;
  generateFormPdf: (data: any) => Promise<File>;
  generateFormPdfData: () => any;
  isLoading: boolean;
  isProcessingUploadingFile?: boolean;
}

const GeneratePdfFormButton: React.FC<GeneratePdfFormButtonProps> = (props) => {
  const authResultRef = useRef<AuthResult | undefined>(undefined);

  function renameFile(originalFile: File, newName: string): File {
    try {
      return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified
      });
    } catch (error) {
      console.error('Error occurred while renaming file:', error);
      return originalFile;
    }
  }

  const validateFile = async (file: File) => {
    const sanitizedFilename = file.name.replace(/\s+/g, '-');
    const sanitizedFile = renameFile(file, sanitizedFilename) as RcFile;

    const validatorOptions = {
      maxFileSizeBytes: 10000000, // 10 MB
    };

    const validator = new FileValidator(sanitizedFile, validatorOptions);
    const result = await validator.validate();

    if (!result.success) {
      console.error(`Error: ${result.message}`);

      switch (result.code) {
        case 'content-type':
          console.log('issue with content-type');
          break;
        case 'content-length':
          console.log('issue with content-length');
          break;
        case 'default':
          message.error(`File did not upload, error: ${JSON.stringify(result.message)}`);
          break;
      }

      return null;
    }

    return result.file;
  };

  const getMetadataHeaders = (metadataFields?: BlobMetadataField[]) => {
    let headers: Record<string, string> = {};
    if (metadataFields) {
      for (const field of metadataFields) {
        headers[`x-ms-meta-${field.name}`] = field.value;
      }
    }
    return headers;
  };

  const getIndexTagsHeaders = (indexTags?: BlobIndexTag[]) => {
    let headers: Record<string, string> = {};
    if (indexTags) {
      const tags = indexTags.reduce((acc: Record<string, string>, tag) => {
        acc[tag.name] = tag.value;
        return acc;
      }, {});
      headers['x-ms-tags'] = new URLSearchParams(tags).toString();
    }
    return headers;
  };

  const uploadFile = async (file: File) => {
    if (authResultRef.current?.isAuthorized) {
      const { authHeader } = authResultRef.current;

      try {
        const metadataHeaders = getMetadataHeaders(authHeader.metadataFields);
        const indexTagsHeaders = getIndexTagsHeaders(authHeader.indexTags);

        const headers = {
          ...metadataHeaders,
          ...indexTagsHeaders,
          Authorization: authHeader.authHeader,
          'x-ms-blob-type': 'BlockBlob',
          'x-ms-version': '2021-04-10',
          'x-ms-date': authHeader.requestDate,
          'Content-Type': file.type
        };

        const response = await axios.put(authHeader.blobPath ?? '', file, {
          headers,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`Upload Progress: ${percentCompleted}%`);
            }
          }
        });

        const blobResponse: AzureBlobResponse = {
          blobUrl: response.request.responseURL,
          versionId: response.headers['x-ms-version-id']
        };

        console.log('uploaded:', file);
        console.log('blobResponse:', blobResponse);

        await props.onSuccess(blobResponse);
      } catch (uploadError) {
        console.error('Error uploading asset: ', uploadError);
        message.error('Failed to upload file');
      }
    }
  };

  const handleGeneratePdf = async () => {
    try {
      const { combinedData} = await props.generateFormPdfData();
      const generatedPdf = await props.generateFormPdf(combinedData);
      const validFile = await validateFile(generatedPdf);

      if (validFile) {
        const authResult = await props.authorizeRequest(validFile);
        if (authResult.isAuthorized) {
          authResultRef.current = authResult;
          await uploadFile(validFile);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Button disabled={props.isProcessingUploadingFile} type="primary" style={{ marginTop: '10px', width: '160px'}} onClick={handleGeneratePdf} loading={props.isLoading}>
      Generate PDF
    </Button>
  );
};

export default GeneratePdfFormButton;
